// Components==============
import { faChevronUp, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import {
  faArrowLeft,
  faBars,
  faFilter,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tooltip from 'components/UI/Tooltip/Tooltip';
import { useMediaQ } from 'hooks/useMediaQ';
import { useTranslate } from 'hooks/useTranslate';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { FilterType } from 'typings';
import { GlobalFilterMenuContext } from 'utilities/GlobalContext/GlobalFilterMenu';
import Checkbox from 'components/UI/Checkbox/Checkbox';
import ConditionalDropdown from './components/Dropdowns/ConditionalDropdown';
import Process from './components/Process/Process';
import Search from './components/Search/Search';
import './FilterMenu.module.scss';
// =========================

type Props = {
  filterValues: FilterType[];
  setFilterValues: React.Dispatch<React.SetStateAction<FilterType[]>>;
  initial: FilterType[];
};

type FilterContextTypes = Props & { triggerReset: boolean };

export const FilterContext = createContext({} as FilterContextTypes);

export default function FilterMenu({
  filterValues,
  setFilterValues,
  initial,
}: Props) {
  const [triggerReset, setTriggerReset] = useState(true);
  const { filterMenuIsOpen, setFilterMenuIsOpen } = useContext(
    GlobalFilterMenuContext
  );

  function handleReset() {
    setFilterValues(initial);
    setTriggerReset((prev) => !prev);
  }

  const translate = useTranslate();

  const query = useMediaQ('min', 1024);
  const width = filterMenuIsOpen ? 230 : 80;

  const collapseCondition = filterMenuIsOpen;

  return (
    <FilterContext.Provider
      value={{ filterValues, setFilterValues, initial, triggerReset }}
    >
      <div styleName="wrapper" style={{ width: query ? width : '100%' }}>
        <div styleName="top">
          <div
            styleName="filter"
            style={{
              cursor: !query && !filterMenuIsOpen ? 'pointer' : 'default',
            }}
            onClick={
              !query && !filterMenuIsOpen
                ? () => setFilterMenuIsOpen(true)
                : undefined
            }
          >
            <FontAwesomeIcon icon={faFilter} />
            {collapseCondition && (
              <p>{translate('filters.filter-menu.menu-title')}</p>
            )}
          </div>
          {collapseCondition && (
            <button type="button" onClick={handleReset}>
              {translate('filters.filter-menu.clear-all-filters')}
            </button>
          )}
        </div>
        <div>
          {collapseCondition &&
            filterValues.map((e, i) => (
              <div styleName="filter-item" key={i}>
                {e.name && (
                  <div styleName="name">
                    <p>{e.name}</p>
                    <Tooltip content={e.tooltip}>
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </Tooltip>
                  </div>
                )}
                {e.type === 'search' ? (
                  <div styleName="mobile-container">
                    <Search value={e.value} id={e.id} />
                  </div>
                ) : e.type === 'process' ? (
                  <Process value={e.value} id={e.id} />
                ) : e.type === 'dropdown' ? (
                  <div styleName="mobile-container">
                    <ConditionalDropdown
                      options={e?.options}
                      id={e?.id}
                      dropdownType={e?.dropdownType}
                      initialText={e.initialText}
                    />
                  </div>
                ) : e.type === 'checkbox' ? (
                  <div styleName="mobile-container">
                    <Checkbox
                      isChecked={e.value}
                      setIsChecked={(check) =>
                        setFilterValues(
                          (prev) =>
                            prev.map((val) =>
                              val.id === e?.id
                                ? { ...val, value: !e.value }
                                : val
                            ) as FilterType[]
                        )
                      }
                    >
                      <p styleName="checkbox">Werk is beoordeeld</p>
                    </Checkbox>
                  </div>
                ) : null}
              </div>
            ))}
        </div>
        {((filterMenuIsOpen && !query) || query) && (
          <div
            styleName="bottom"
            onClick={() => setFilterMenuIsOpen((prev) => !prev)}
            style={{
              cursor: !query && filterMenuIsOpen ? 'pointer' : 'default',
            }}
          >
            <button type="button">
              {!query ? (
                <FontAwesomeIcon icon={faChevronUp} />
              ) : (
                <>
                  {filterMenuIsOpen ? (
                    <FontAwesomeIcon icon={faArrowLeft} />
                  ) : (
                    <FontAwesomeIcon icon={faBars} />
                  )}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </FilterContext.Provider>
  );
}
